import React, { Suspense, lazy, useEffect } from 'react';
import { Routes, Route, Link, Navigate } from 'react-router-dom';
import { Spinner } from '@/components/ui/Spinner';
import { useAuthStore } from '@/stores/authStore';
import RequireAuth from '@/components/auth/RequireAuth';
import RequireAdmin from '@/components/auth/RequireAdmin';
// Import for side effect: applies the persisted theme (dark class + data-theme) to <html> on app load.
import '@/stores/uiStore';
import { useThemeSettings } from '@/hooks/useThemeSettings';
import { useUserAppearanceSync } from '@/hooks/useUserAppearanceSync';
import { LanguageProvider } from '@/context/LanguageContext';
import { MaintenanceGate } from '@/components/maintenance/MaintenanceGate';

// Applies the signed-in user's server-persisted theme/language to the UI
// stores once per user load. Must render inside <LanguageProvider>.
const UserAppearanceSync = () => {
    useUserAppearanceSync();
    return null;
};

// Helper to handle both default and named exports
const lazyPage = (importFn: () => Promise<any>, name?: string) =>
    lazy(() => importFn().then((m: any) => ({ default: m.default || m[name as keyof typeof m] || m })));

// Lazy-loaded page components
const LandingPage = lazyPage(() => import('./pages/LandingPage'), 'LandingPage');
const LoginPage = lazyPage(() => import('./pages/auth/LoginPage'), 'LoginPage');
const RegisterPage = lazyPage(() => import('./pages/auth/RegisterPage'), 'RegisterPage');
const ForgotPasswordPage = lazyPage(() => import('./pages/auth/ForgotPasswordPage'), 'ForgotPasswordPage');
const VerifyEmailPage = lazyPage(() => import('./pages/auth/VerifyEmailPage'), 'VerifyEmailPage');
const OAuthCallbackPage = lazyPage(() => import('./pages/auth/OAuthCallbackPage'), 'OAuthCallbackPage');
const ResetPasswordPage = lazyPage(() => import('./pages/auth/ResetPasswordPage'), 'ResetPasswordPage');
const DashboardPage = lazyPage(() => import('./pages/DashboardPage'), 'DashboardPage');
const StreamPage = lazyPage(() => import('./pages/stream/StreamPage'), 'StreamPage');
const PricingPage = lazyPage(() => import('./pages/pricing/PricingPage'), 'PricingPage');
const ManageSubscriptionPage = lazyPage(() => import('./pages/subscription/ManageSubscriptionPage'), 'ManageSubscriptionPage');
const TermsPage = lazyPage(() => import('./pages/TermsPage'), 'TermsPage');
const PrivacyPage = lazyPage(() => import('./pages/PrivacyPage'), 'PrivacyPage');
const FriendsPage = lazyPage(() => import('./pages/friends/FriendsPage'), 'FriendsPage');
const AddFriendPage = lazyPage(() => import('./pages/friends/AddFriendPage'), 'AddFriendPage');
const MarketplacePage = lazyPage(() => import('./pages/marketplace/MarketplacePage'), 'MarketplacePage');
const ForumPage = lazyPage(() => import('./pages/forum/ForumPage'), 'ForumPage');

// Public pages
const FeaturesPage = lazyPage(() => import('./pages/FeaturesPage'), 'FeaturesPage');
const HelpPage = lazyPage(() => import('./pages/HelpPage'), 'HelpPage');
const DocsPage = lazyPage(() => import('./pages/DocsPage'), 'DocsPage');
const AboutPage = lazyPage(() => import('./pages/AboutPage'), 'AboutPage');
const ContactPage = lazyPage(() => import('./pages/ContactPage'), 'ContactPage');
const AuthRegisterPage = lazyPage(() => import('./pages/AuthRegisterPage'), 'AuthRegisterPage');

// Admin sub-pages
const AdminDashboardPage = lazyPage(() => import('./pages/admin/AdminDashboardPage'), 'AdminDashboardPage');
const AdminUsersPage = lazyPage(() => import('./pages/admin/AdminUsersPage'), 'AdminUsersPage');
const AdminUserCreatePage = lazyPage(() => import('./pages/admin/AdminUserCreatePage'), 'AdminUserCreatePage');
const AdminUserDetailPage = lazyPage(() => import('./pages/admin/AdminUserDetailPage'), 'AdminUserDetailPage');
const AdminStreamsPage = lazyPage(() => import('./pages/admin/AdminStreamsPage'), 'AdminStreamsPage');
const AdminContentPage = lazyPage(() => import('./pages/admin/AdminContentPage'), 'AdminContentPage');
const AdminReportsPage = lazyPage(() => import('./pages/admin/AdminReportsPage'), 'AdminReportsPage');
const AdminPaymentsPage = lazyPage(() => import('./pages/admin/AdminPaymentsPage'), 'AdminPaymentsPage');
const AdminSubscriptionsPage = lazyPage(() => import('./pages/admin/AdminSubscriptionsPage'), 'AdminSubscriptionsPage');
const AdminNewslettersPage = lazyPage(() => import('./pages/admin/AdminNewslettersPage'), 'AdminNewslettersPage');
const AdminLogsPage = lazyPage(() => import('./pages/admin/AdminLogsPage'), 'AdminLogsPage');
const AdminSettingsPage = lazyPage(() => import('./pages/admin/AdminSettingsPage'), 'AdminSettingsPage');
const AdvertisementsPage = lazyPage(() => import('./pages/admin/AdvertisementsPage'), 'AdvertisementsPage');
const AppearancePage = lazyPage(() => import('./pages/admin/AppearancePage'), 'AppearancePage');
const AdminSeoPage = lazyPage(() => import('./pages/admin/AdminSeoPage'), 'AdminSeoPage');
const AdminSecurityPage = lazyPage(() => import('./pages/admin/security/AdminSecurityPage'), 'AdminSecurityPage');
const AdminOptimizationsPage = lazyPage(() => import('./pages/admin/optimization/AdminOptimizationsPage'), 'AdminOptimizationsPage');

// Marketplace sub-pages
const CartPage = lazyPage(() => import('./pages/marketplace/CartPage'), 'CartPage');
const CheckoutPage = lazyPage(() => import('./pages/marketplace/CheckoutPage'), 'CheckoutPage');
const MarketplaceItemPage = lazyPage(() => import('./pages/marketplace/MarketplaceItemPage'), 'MarketplaceItemPage');
const MarketplaceSellPage = lazyPage(() => import('./pages/marketplace/MarketplaceSellPage'), 'MarketplaceSellPage');

// Notifications
const NotificationsPage = lazyPage(() => import('./pages/notifications/NotificationsPage'), 'NotificationsPage');

// P2P
const P2PConnectionsPage = lazyPage(() => import('./pages/p2p/P2PConnectionsPage'), 'P2PConnectionsPage');

// Chat
const ChatPage = lazyPage(() => import('./pages/chat/ChatPage'), 'ChatPage');

// Forum
const ForumCreatePage = lazyPage(() => import('./pages/forum/ForumCreatePage'), 'ForumCreatePage');
const ForumThreadPage = lazyPage(() => import('./pages/forum/ForumThreadPage'), 'ForumThreadPage');
const ForumTopicPage = lazyPage(() => import('./pages/forum/ForumTopicPage'), 'ForumTopicPage');
const NewForumPostPage = lazyPage(() => import('./pages/forum/NewForumPostPage'), 'NewForumPostPage');

// Settings sub-pages
const SettingsProfilePage = lazyPage(() => import('./pages/settings/SettingsProfilePage'), 'SettingsProfilePage');
const SettingsPrivacyPage = lazyPage(() => import('./pages/settings/SettingsPrivacyPage'), 'SettingsPrivacyPage');
const SettingsSecurityPage = lazyPage(() => import('./pages/settings/SettingsSecurityPage'), 'SettingsSecurityPage');
const SettingsNotificationsPage = lazyPage(() => import('./pages/settings/SettingsNotificationsPage'), 'SettingsNotificationsPage');
const SettingsAppearancePage = lazyPage(() => import('./pages/settings/SettingsAppearancePage'), 'SettingsAppearancePage');
const SettingsApiKeysPage = lazyPage(() => import('./pages/settings/SettingsApiKeysPage'), 'SettingsApiKeysPage');
const SettingsNetworkPage = lazyPage(() => import('./pages/settings/SettingsNetworkPage'), 'SettingsNetworkPage');

// Studio sub-pages
const AnalyticsPage = lazyPage(() => import('./pages/studio/AnalyticsPage'), 'AnalyticsPage');
const SchedulePage = lazyPage(() => import('./pages/studio/SchedulePage'), 'SchedulePage');
const VideoStudioPage = lazyPage(() => import('./pages/studio/VideoStudioPage'), 'VideoStudioPage');
const BroadcastTemplatesPage = lazyPage(() => import('./pages/studio/BroadcastTemplatesPage'), 'BroadcastTemplatesPage');
const BroadcastPage = lazyPage(() => import('./pages/BroadcastPage'), 'BroadcastPage');
const VideoEditorPage = lazyPage(() => import('./pages/studio/VideoEditorPage'), 'VideoEditorPage');

// Profile sub-pages
const EditProfilePage = lazyPage(() => import('./pages/profile/EditProfilePage'), 'EditProfilePage');
const DonationsPage = lazyPage(() => import('./pages/profile/DonationsPage'), 'DonationsPage');
const ChangePasswordPage = lazyPage(() => import('./pages/profile/ChangePasswordPage'), 'ChangePasswordPage');
const UserProfilePage = lazyPage(() => import('./pages/profile/UserProfilePage'), 'UserProfilePage');

// Stream sub-pages
const BrowseStreamsPage = lazyPage(() => import('./pages/stream/BrowseStreamsPage'), 'BrowseStreamsPage');

// My content pages
const MyVideosPage = lazyPage(() => import('./pages/MyVideosPage'), 'MyVideosPage');
const UploadPage = lazyPage(() => import('./pages/UploadPage'), 'UploadPage');
const MyStreamsPage = lazyPage(() => import('./pages/MyStreamsPage'), 'MyStreamsPage');
const PhotosPage = lazyPage(() => import('./pages/photos/PhotosPage'), 'PhotosPage');
const ClipsPage = lazyPage(() => import('./pages/clips/ClipsPage'), 'ClipsPage');
const VideoDetailPage = lazyPage(() => import('./pages/VideoDetailPage'), 'VideoDetailPage');

const LoadingFallback = () => (
    <div className="flex items-center justify-center min-h-screen bg-base-200">
        <Spinner size="lg" />
    </div>
);

const NotFoundPage = () => (
    <div className="flex items-center justify-center min-h-screen bg-base-200">
        <div className="text-center">
            <h1 className="text-6xl font-bold text-base-content/20">404</h1>
            <p className="text-xl mt-4">Page not found</p>
            <Link to="/" className="btn btn-primary mt-4">Go Home</Link>
        </div>
    </div>
);

const App: React.FC = () => {
    const fetchUser = useAuthStore((state) => state.fetchUser);

    // Fetch server-managed theme/content settings and apply CSS custom properties.
    useThemeSettings();

    // Restore the user object on full page reload. The token is persisted in
    // localStorage, but `user` is not — so re-fetch it from /api/v1/user when
    // a token exists. If the token is stale, fetchUser clears auth state.
    useEffect(() => {
        if (localStorage.getItem('auth_token')) {
            fetchUser();
        }
    }, [fetchUser]);

    return (
        <LanguageProvider>
            <UserAppearanceSync />
            <MaintenanceGate>
        <Suspense fallback={<LoadingFallback />}>
            <Routes>
                {/* Public Routes */}
                <Route path="/" element={<LandingPage />} />
                <Route path="/pricing" element={<PricingPage />} />
                <Route path="/pricing/compare" element={<PricingPage />} />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route path="/features" element={<FeaturesPage />} />
                <Route path="/help" element={<HelpPage />} />
                <Route path="/docs" element={<DocsPage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route path="/auth/register" element={<AuthRegisterPage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/verify-email" element={<VerifyEmailPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route path="/auth/oauth/callback" element={<OAuthCallbackPage />} />

                {/* Authenticated Routes — guests are redirected to /login */}
                <Route element={<RequireAuth />}>
                    <Route path="/dashboard" element={<DashboardPage />} />
                    <Route path="/stream/:id" element={<StreamPage />} />
                    {/* Legacy alias — redirects to the canonical /browse route */}
                    <Route path="/stream/browse" element={<Navigate to="/browse" replace />} />
                    <Route path="/browse" element={<BrowseStreamsPage />} />
                    <Route path="/browse/categories" element={<BrowseStreamsPage />} />
                    <Route path="/browse/live" element={<BrowseStreamsPage />} />
                    <Route path="/streams/mine" element={<MyStreamsPage />} />
                    <Route path="/streams/scheduled" element={<SchedulePage />} />
                    <Route path="/studio/live" element={<BroadcastPage />} />
                    <Route path="/analytics" element={<AnalyticsPage />} />
                    <Route path="/streams" element={<Navigate to="/browse" replace />} />
                    <Route path="/studio" element={<VideoStudioPage />} />
                    <Route path="/broadcast-templates" element={<BroadcastTemplatesPage />} />
                    <Route path="/upload" element={<UploadPage />} />
                    <Route path="/videos" element={<MyVideosPage />} />
                    <Route path="/video/:id" element={<VideoDetailPage />} />
                    <Route path="/photos" element={<PhotosPage />} />
                    <Route path="/clips" element={<ClipsPage />} />
                    <Route path="/profile" element={<EditProfilePage />} />
                    <Route path="/profile/:username" element={<UserProfilePage />} />
                    <Route path="/donations" element={<DonationsPage />} />
                    <Route path="/friends" element={<FriendsPage />} />
                    <Route path="/friends/add" element={<AddFriendPage />} />
                    <Route path="/marketplace" element={<MarketplacePage />} />
                    <Route path="/settings" element={<SettingsProfilePage />} />

                    {/* Forum Routes */}
                    <Route path="/forum" element={<ForumPage />} />
                    <Route path="/forum/:id" element={<ForumThreadPage />} />
                    <Route path="/forum/:id/topic" element={<ForumTopicPage />} />
                    <Route path="/forum/new" element={<NewForumPostPage />} />
                    <Route path="/forum/recent" element={<ForumPage />} />
                    <Route path="/forum/my-posts" element={<ForumPage />} />
                    <Route path="/forum/groups" element={<ForumPage />} />

                    {/* Forum Create — admin only */}
                    <Route element={<RequireAdmin />}>
                        <Route path="/forum/create" element={<ForumCreatePage />} />
                    </Route>

                    {/* Marketplace Routes */}
                    <Route path="/marketplace/cart" element={<CartPage />} />
                    <Route path="/marketplace/checkout" element={<CheckoutPage />} />
                    <Route path="/marketplace/item/:id" element={<MarketplaceItemPage />} />
                    <Route path="/marketplace/sell" element={<MarketplaceSellPage />} />

                    {/* Notifications Routes */}
                    <Route path="/notifications" element={<NotificationsPage />} />

                    {/* P2P Routes */}
                    <Route path="/p2p" element={<P2PConnectionsPage />} />
                    <Route path="/p2p/peers" element={<P2PConnectionsPage />} />
                    <Route path="/p2p/stats" element={<P2PConnectionsPage />} />

                    {/* Chat Routes */}
                    <Route path="/chat" element={<ChatPage />} />
                    <Route path="/chat/groups" element={<ChatPage />} />
                    <Route path="/chat/blocked" element={<ChatPage />} />

                    {/* Settings Routes */}
                    <Route path="/settings/profile" element={<SettingsProfilePage />} />
                    <Route path="/settings/privacy" element={<SettingsPrivacyPage />} />
                    <Route path="/settings/security" element={<SettingsSecurityPage />} />
                    <Route path="/settings/notifications" element={<SettingsNotificationsPage />} />
                    <Route path="/settings/appearance" element={<SettingsAppearancePage />} />
                    <Route path="/settings/api-keys" element={<SettingsApiKeysPage />} />
                    <Route path="/settings/network" element={<SettingsNetworkPage />} />

                    {/* Studio Routes */}
                    <Route path="/studio/analytics" element={<AnalyticsPage />} />
                    <Route path="/studio/schedule" element={<SchedulePage />} />
                    <Route path="/studio/videos" element={<MyVideosPage />} />
                    <Route path="/studio/edit" element={<VideoEditorPage />} />
                    <Route path="/broadcast" element={<BroadcastPage />} />

                    {/* Profile Routes */}
                    <Route path="/profile/me" element={<UserProfilePage />} />
                    <Route path="/profile/edit" element={<EditProfilePage />} />
                    <Route path="/profile/:username/change-password" element={<ChangePasswordPage />} />
                    <Route path="/profile/:username/view" element={<UserProfilePage />} />
                    <Route path="/profile/content" element={<MyVideosPage />} />
                    <Route path="/profile/followers" element={<UserProfilePage />} />

                    {/* Friends Routes */}
                    <Route path="/friends/requests" element={<FriendsPage />} />

                    {/* Notifications Routes (sub-routes) */}
                    <Route path="/notifications/mentions" element={<NotificationsPage />} />
                    <Route path="/notifications/likes" element={<NotificationsPage />} />
                    <Route path="/notifications/comments" element={<NotificationsPage />} />
                    <Route path="/notifications/friends" element={<NotificationsPage />} />

                    {/* Pricing Routes (sub-routes) */}
                    <Route path="/pricing/my-subscription" element={<PricingPage />} />
                    <Route path="/pricing/:planId/subscribe" element={<PricingPage />} />

                    {/* Subscription management (auth-only) */}
                    <Route path="/subscription/manage" element={<ManageSubscriptionPage />} />

                    {/* Marketplace Routes (sub-routes) */}
                    <Route path="/marketplace/purchases" element={<MarketplacePage />} />

                    {/* Stream sub-routes */}
                    <Route path="/stream/chat" element={<ChatPage />} />
                    <Route path="/stream/info" element={<StreamPage />} />
                    <Route path="/stream/moderation" element={<StreamPage />} />

                    {/* Admin Routes — additionally require is_admin */}
                    <Route element={<RequireAdmin />}>
                        <Route path="/admin" element={<AdminDashboardPage />} />
                        <Route path="/admin/dashboard" element={<AdminDashboardPage />} />
                        <Route path="/admin/users" element={<AdminUsersPage />} />
                        <Route path="/admin/streams" element={<AdminStreamsPage />} />
                        <Route path="/admin/reports" element={<AdminReportsPage />} />
                        <Route path="/admin/settings" element={<AdminSettingsPage />} />
                        <Route path="/admin/subscriptions" element={<AdminSubscriptionsPage />} />
                        <Route path="/admin/newsletters" element={<AdminNewslettersPage />} />
                        <Route path="/admin/content" element={<AdminContentPage />} />
                        <Route path="/admin/payments" element={<AdminPaymentsPage />} />
                        <Route path="/admin/advertisements" element={<AdvertisementsPage />} />
                        <Route path="/admin/appearance" element={<AppearancePage />} />
                        <Route path="/admin/logs" element={<AdminLogsPage />} />
                        <Route path="/admin/seo" element={<AdminSeoPage />} />
                        <Route path="/admin/security" element={<AdminSecurityPage />} />
                        <Route path="/admin/optimizations" element={<AdminOptimizationsPage />} />
                        <Route path="/admin/users/create" element={<AdminUserCreatePage />} />
                        <Route path="/admin/users/:id" element={<AdminUserDetailPage />} />
                    </Route>
                </Route>

                {/* 404 */}
                <Route path="*" element={<NotFoundPage />} />

            </Routes>
        </Suspense>
            </MaintenanceGate>
        </LanguageProvider>
    );
};

export default App;
